import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import UserManagement from "@/pages/UserManagement";
import Employees from "@/pages/Employees";
import Sites from "@/pages/Sites";
import Attendance from "@/pages/Attendance";
import DeskSessions from "@/pages/DeskSessions";
import Reviews from "@/pages/Reviews";
import Billing from "@/pages/Billing";
import NotificationSettings from "@/pages/NotificationSettings";
import PushBroadcast from "@/pages/PushBroadcast";
import Reports from "@/pages/Reports";
import Journey from "@/pages/Journey";
import Businesses from "@/pages/Businesses";
import HelpManual from "@/pages/HelpManual";
import Support from "@/pages/Support";
import Privacy from "@/pages/Privacy";
import Landing from "@/pages/Landing";

function Booting() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
    </div>
  );
}

function Protected({ children }) {
  const { me, booting } = useAuth();
  if (booting) return <Booting />;
  if (!me) return <Navigate to="/login" replace />;
  return children;
}

function LoginGate() {
  const { me, booting } = useAuth();
  if (booting) return <Booting />;
  if (me) return <Navigate to="/dashboard" replace />;
  return <Login />;
}

function SuperAdminRoute({ children }) {
  const { me } = useAuth();
  if (me?.role !== "super_admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function PermRoute({ perm, children }) {
  const { me } = useAuth();
  if (me?.role === "super_admin") return children;
  if ((me?.permissions || []).includes(perm)) return children;
  return <Navigate to="/dashboard" replace />;
}

function FeatureRoute({ perm, feature, children }) {
  const { me } = useAuth();
  if (me?.role === "super_admin") return children;
  if (feature && me?.features && me.features[feature] === false) return <Navigate to="/dashboard" replace />;
  if ((me?.permissions || []).includes(perm)) return children;
  return <Navigate to="/dashboard" replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<LoginGate />} />
          <Route path="/support" element={<Support />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route
            element={
              <Protected>
                <Layout />
              </Protected>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/users" element={<SuperAdminRoute><UserManagement /></SuperAdminRoute>} />
            <Route path="/employees" element={<PermRoute perm="employees"><Employees /></PermRoute>} />
            <Route path="/sites" element={<PermRoute perm="sites"><Sites /></PermRoute>} />
            <Route path="/attendance" element={<PermRoute perm="attendance"><Attendance /></PermRoute>} />
            <Route path="/desk-sessions" element={<PermRoute perm="attendance"><DeskSessions /></PermRoute>} />
            <Route path="/reviews" element={<PermRoute perm="reviews"><Reviews /></PermRoute>} />
            <Route path="/billing" element={<SuperAdminRoute><Billing /></SuperAdminRoute>} />
            <Route path="/notifications" element={<SuperAdminRoute><NotificationSettings /></SuperAdminRoute>} />
            <Route path="/push-broadcast" element={<SuperAdminRoute><PushBroadcast /></SuperAdminRoute>} />
            <Route path="/reports" element={<PermRoute perm="reports"><Reports /></PermRoute>} />
            <Route path="/journey" element={<FeatureRoute perm="reports" feature="journey_history"><Journey /></FeatureRoute>} />
            <Route path="/businesses" element={<PermRoute perm="businesses"><Businesses /></PermRoute>} />
            <Route path="/help" element={<SuperAdminRoute><HelpManual /></SuperAdminRoute>} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
